import React, { useState, useRef, useEffect, useCallback } from 'react';
import getProps from 'containers/getProps';
import PropTypes from 'prop-types';
import styles from './styles.scss';
import { useTwilio } from 'providers/TwilioProvider/hooks/public/useTwilio';
import {
  Avatar,
  Badge,
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  OutlinedInput,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';
import { useMessages } from 'features/messaging/hooks';
import { doesSentenceContainRestrictedTerm } from 'helpers';
import AiGeneratedMessage from 'features/assistant-ai/components/personalized-message';

const TypingArea = ({
  conversation,
  tablet,
  forwardFocus,
  closeSearch,
  userId,
  estheticianId,
}) => {
  const { sendMessage } = useTwilio();
  const {
    messaging: { message: defaultMessage },
    actions: { removeMessage },
  } = useMessages();

  const [mediaFiles, setMediaFiles] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState(defaultMessage);

  const textArea = useRef(null);
  const inputFile = useRef(null);

  const focusOnTypingArea = () => {
    if (textArea.current && closeSearch) {
      textArea.current.focus();
    }
  };

  const removeMedia = idx => {
    setMediaFiles(curr => curr.filter((_, i) => i !== idx));
  };

  useEffect(() => {
    if (!tablet || forwardFocus) {
      focusOnTypingArea();
    }
  }, [conversation, tablet, forwardFocus]);

  useEffect(() => {
    if (!conversation?.sid || message) return;

    const savedMessage = localStorage.getItem(conversation.sid);
    setMessage(savedMessage ?? '');
  }, [conversation?.sid]);

  const handleUpdateMessage = evt => {
    const { value } = evt.target;
    setMessage(value);

    if (!conversation?.sid) return;

    if (!value) {
      localStorage.removeItem(conversation.sid);
      return;
    }

    localStorage.setItem(conversation.sid, value);
  };

  const sendMediaMessage = useCallback(
    async (media, contentType) => {
      if (!media) return;

      await sendMessage(conversation.sid, {
        contentType,
        media,
      });
    },
    [conversation]
  );

  const sendTextMessage = useCallback(
    async newMessage => {
      if (!newMessage) return;
      await sendMessage(conversation.sid, newMessage);
    },
    [conversation]
  );

  const handleSendMessage = e => {
    e.preventDefault();

    if (message && doesSentenceContainRestrictedTerm(message)) {
      showError(
        "Sorry! Due to federal guidelines, we're not allowed to send messages with words related to cannabis, including CBD. Please remove any related language and try again.",
        5000
      );
      return;
    }

    if (message) {
      sendTextMessage(message);
    }
    if (mediaFiles.length) {
      mediaFiles.forEach(({ file }) => {
        sendMediaMessage(file, file?.type || 'image/*');
      });
    }
    setMessage('');
    setMediaFiles([]);
    if (defaultMessage) removeMessage();
    localStorage.removeItem(conversation.sid);
  };

  const loadImage = () => {
    inputFile.current.click();
  };

  const getBase64 = file => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const showError = (message, timeout = 500) => {
    setError(message);
    setTimeout(() => {
      setError('');
    }, timeout);
  };

  const handleImageUpload = async e => {
    e.preventDefault();
    const { files } = e.target;
    if (!files?.length) return;

    for (const file of e.target.files) {
      const fileMB = file.size / Math.pow(1024, 2);

      const fileName = file.name.split('.');
      const ext = fileName.pop();
      const blob = file.slice(0, file.size, 'image/*');
      const newFile = new File([blob], fileName, {
        type: `image/${ext}`,
      });

      if (fileMB > 150) {
        showError('The image is too large to be upload');
        return;
      }

      getBase64(file)
        .then(src => {
          setMediaFiles(curr => [
            ...curr,
            { file: newFile, preview: src, type: file.type },
          ]);
        })
        .catch(() => {
          showError('Error Loading the image');
        });
    }

    inputFile.current.value = '';
    focusOnTypingArea();
  };

  const handleSendOnEnter = e => {
    // If it's enter and shift key was not pressed then send the message
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  return (
    <>
      <Box
        sx={{
          width: 1,
          mb: 1,
          pt: 1.5,
          overflowX: 'scroll',
          background: 'transparent',
          display: 'flex',
          flexWrap: 'nowrap',
          mx: 1,
        }}
      >
        {mediaFiles.map((m, i) => (
          <Badge
            badgeContent={
              <CloseIcon
                size={'sm'}
                fontSize={'0.95rem'}
                onClick={() => removeMedia(i)}
                sx={{ cursor: 'pointer' }}
              />
            }
            sx={{
              mx: 0.5,
              '& .MuiBadge-badge': {
                width: '1rem',
              },
            }}
            key={i}
            color="error"
          >
            <Avatar
              src={m.preview}
              variant="rounded"
              sx={{
                width: 75,
                height: 75,
                backgroundColor: 'background.default',
                p: 0.5,
              }}
            />
          </Badge>
        ))}
      </Box>
      <Box classes={styles.Container}>
        <FormControl fullWidth sx={{ px: 1 }}>
          {userId && estheticianId && (
            <AiGeneratedMessage
              onChange={handleUpdateMessage}
              userId={userId}
              estheticianId={estheticianId}
            />
          )}
          <OutlinedInput
            fullWidth
            multiline
            id="textArea"
            disabled={!conversation}
            // ref={textArea}
            onKeyDown={handleSendOnEnter}
            value={message}
            onChange={handleUpdateMessage}
            placeholder="Send Message"
            error={!!error}
            endAdornment={
              <>
                <IconButton
                  aria-label="Add Image"
                  disabled={!conversation}
                  onClick={loadImage}
                  sx={{ color: 'primary.main' }}
                >
                  <ImageIcon />
                </IconButton>
                <Button
                  color={'primary'}
                  variant={'contained'}
                  disabled={!conversation}
                  onClick={handleSendMessage}
                >
                  Send
                </Button>
              </>
            }
          />
          <FormHelperText>{error}</FormHelperText>
        </FormControl>
      </Box>
      <Box classes={styles.Tools}>
        <Box classes={styles.Files}>
          <input
            disabled={!conversation}
            accept="image/*"
            ref={inputFile}
            id="loadImage"
            type="file"
            style={{ display: 'none' }}
            onChange={handleImageUpload}
            multiple={true}
          />
        </Box>
      </Box>
    </>
  );
};

TypingArea.propTypes = {
  forwardFocus: PropTypes.bool,
  conversation: PropTypes.any,
  tablet: PropTypes.bool,
  identity: PropTypes.string.isRequired,
  esthetician: PropTypes.bool,
  messages: PropTypes.arrayOf(PropTypes.object),
  messageActions: PropTypes.any,
  closeSearch: PropTypes.bool,
  userId: PropTypes.string,
  estheticianId: PropTypes.string,
};

TypingArea.displayName = 'TypingArea';

export default getProps('messageActions')(TypingArea);
